import { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  TablePagination,
  alpha,
  Tabs,
  Tab,
  Drawer,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { TableToolbar, ColumnConfig } from '../../components/TableToolbar';
import { ProductInventoryDialog } from '../../components/ProductInventoryDialog';
import { LimitWarningBanner } from '../../components/LimitWarningBanner';
import { useB2BAccess } from '../../hooks/useModuleAccess';
import type { Product } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import { ProductSearch } from '../../components/ProductSearch';
import { ProductFilters } from '../../components/ProductFilters';
import { RecentSearches } from '../../components/RecentSearches';
import { useProductSearch, ProductSearchParams, StockStatus, type RecentSearch } from '../../hooks/useProductSearch';
import { BulkActionsToolbar } from '../../components/products/BulkActionsToolbar';
import { BulkOperationProgress } from '../../components/products/BulkOperationProgress';
import { BarcodeProductSearch } from '../../components/barcode/BarcodeProductSearch';
import { searchProductByBarcode } from '../../hooks/useBarcodeProductSearch';

const fetchProducts = async (page: number, pageSize: number) => {
  const response = await axiosInstance.get(`/products/admin`, {
    params: {
      page: page + 1,
      limit: pageSize,
    },
  });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const exportColumns: ColumnConfig[] = [
  { field: 'name', headerName: 'Product Name' },
  { field: 'sku', headerName: 'SKU' },
  { field: 'barcode', headerName: 'Barcode' },
  { field: 'description', headerName: 'Description' },
  { field: 'category', headerName: 'Category' },
  {
    field: 'price',
    headerName: 'Price',
    valueFormatter: (value) => {
      const num = Number(value) || 0;
      return `${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DZD`;
    },
  },
  {
    field: 'cost',
    headerName: 'Cost',
    valueFormatter: (value) => {
      if (!value) return '-';
      const num = Number(value) || 0;
      return `${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DZD`;
    },
  },
  {
    field: 'stockQuantity',
    headerName: 'Stock Quantity',
    valueFormatter: (value) => String(value ?? 0),
  },
  {
    field: 'lowStockThreshold',
    headerName: 'Low Stock Threshold',
    valueFormatter: (value) => String(value ?? 0),
  },
  {
    field: 'productType',
    headerName: 'Product Type',
    valueFormatter: (value) => {
      if (value === 'sellable') return 'Sellable';
      if (value === 'internal') return 'Internal';
      return '-';
    },
  },
  {
    field: 'isActive',
    headerName: 'Active',
    valueFormatter: (value) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'isVisibleInStorefront',
    headerName: 'Visible in Storefront',
    valueFormatter: (value) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'isB2bVisible',
    headerName: 'B2B Visible',
    valueFormatter: (value, row) => ((row as any).isB2bVisible ? 'Yes' : 'No'),
  },
  {
    field: 'b2bPrice',
    headerName: 'B2B Price',
    valueFormatter: (value, row) => {
      const b2bPrice = (row as any).b2bPrice;
      if (!b2bPrice) return '-';
      const num = Number(b2bPrice) || 0;
      return `${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DZD`;
    },
  },
  {
    field: 'b2bMinimumQuantity',
    headerName: 'B2B Min Quantity',
    valueFormatter: (value, row) => {
      const minQty = (row as any).b2bMinimumQuantity;
      return minQty ? String(minQty) : '-';
    },
  },
  {
    field: 'location',
    headerName: 'Location',
    valueFormatter: (value, row) => (value?.name || row.locationId || '-'),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    valueFormatter: (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    valueFormatter: (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
];

type ProductTab = 'all' | 'public' | 'b2b';

export const ProductList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const { hasAccess: hasB2BAccess } = useB2BAccess();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [selectedProductForInventory, setSelectedProductForInventory] = useState<Product | null>(null);
  const [activeTab, setActiveTab] = useState<ProductTab>('all');

  // Multi-select state
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  // Advanced filter state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minPrice, setMinPrice] = useState<number | undefined>();
  const [maxPrice, setMaxPrice] = useState<number | undefined>();
  const [stockStatus, setStockStatus] = useState<StockStatus>(StockStatus.ALL);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [shouldSaveSearch, setShouldSaveSearch] = useState(false);

  // Undo state
  const [undoSnackbarOpen, setUndoSnackbarOpen] = useState(false);
  const [undoOperationId, setUndoOperationId] = useState<string | null>(null);
  const [undoOperationType, setUndoOperationType] = useState<string>('');
  const [undoTimeoutId, setUndoTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Bulk action dialog state
  const [bulkPriceDialogOpen, setBulkPriceDialogOpen] = useState(false);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkStockDialogOpen, setBulkStockDialogOpen] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);

  // Bulk operation progress state
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [bulkOperationProgress, setBulkOperationProgress] = useState<{
    successCount: number;
    failureCount: number;
    total: number;
  } | null>(null);

  // Bulk action form state
  const [priceAdjustment, setPriceAdjustment] = useState<number>(0);
  const [priceAdjustmentType, setPriceAdjustmentType] = useState<'percentage' | 'fixed'>('percentage');
  const [newCategory, setNewCategory] = useState<string>('');
  const [stockAdjustment, setStockAdjustment] = useState<number>(0);
  const [stockOperation, setStockOperation] = useState<'add' | 'subtract' | 'set'>('add');

  // Barcode scanner input state
  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['products', page, pageSize],
    queryFn: () => fetchProducts(page, pageSize),
  });

  // Build search params for advanced filters
  const searchParamsObj: ProductSearchParams = useMemo(() => {
    const params: ProductSearchParams = {
      page: page + 1,
      limit: pageSize,
    };
    if (searchValue) params.q = searchValue;
    if (selectedCategory) params.category = selectedCategory;
    if (minPrice !== undefined) params.minPrice = minPrice;
    if (maxPrice !== undefined) params.maxPrice = maxPrice;
    if (stockStatus !== StockStatus.ALL) params.stockStatus = stockStatus;

    // Add tab-specific filters
    if (activeTab === 'public') {
      params.isVisibleInStorefront = true;
    } else if (activeTab === 'b2b') {
      params.isB2bVisible = true;
    }

    // Only save search when explicitly requested (Apply button clicked)
    if (shouldSaveSearch) {
      params.saveSearch = true;
    }

    return params;
  }, [searchValue, selectedCategory, minPrice, maxPrice, stockStatus, activeTab, page, pageSize, shouldSaveSearch]);

  // Use search hook if advanced filters are active
  const hasAdvancedFilters = !!(selectedCategory || minPrice || maxPrice || stockStatus !== StockStatus.ALL);
  const { data: searchResults, isLoading: searchLoading, isFetched } = useProductSearch(
    hasAdvancedFilters || searchValue ? searchParamsObj : undefined
  );

  // Reset shouldSaveSearch after search is done
  if (isFetched && shouldSaveSearch) {
    setShouldSaveSearch(false);
  }

  // Use search loading state when filters are active
  const productsLoading = (hasAdvancedFilters || searchValue) ? searchLoading : isLoading;

  const filteredData = useMemo(() => {
    // If using backend search/filters, return search results directly
    if ((hasAdvancedFilters || searchValue) && searchResults?.data) {
      return searchResults.data;
    }

    // Otherwise, apply client-side filters to regular products
    if (!data?.data) return [];

    return data.data.filter((item: Product) => {
      // Tab filter (only needed when not using backend search)
      if (activeTab === 'public') {
        if (!(item as any).isVisibleInStorefront) return false;
      } else if (activeTab === 'b2b') {
        if (!(item as any).isB2bVisible) return false;
      }

      return true;
    });
  }, [data?.data, searchResults, searchValue, activeTab, hasAdvancedFilters]);

  // Count products for tab badges
  const tabCounts = useMemo(() => {
    if (!data?.data) return { all: 0, public: 0, b2b: 0 };
    return {
      all: data.data.length,
      public: data.data.filter((p: Product) => (p as any).isVisibleInStorefront).length,
      b2b: data.data.filter((p: Product) => (p as any).isB2bVisible).length,
    };
  }, [data?.data]);

  // Extract unique categories for filters
  const uniqueCategories = useMemo(() => {
    if (!data?.data) return [];
    const categories = new Set<string>();
    data.data.forEach((product: Product) => {
      if (product.category) {
        categories.add(product.category);
      }
    });
    return Array.from(categories).sort();
  }, [data?.data]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: ProductTab) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<Product> }) =>
      axiosInstance.patch(`/products/${id}`, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showMessage('Product updated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update product', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showMessage('Product deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to delete product', 'error');
    },
  });

  const undoMutation = useMutation({
    mutationFn: (operationId: string) => axiosInstance.post(`/products/bulk/undo/${operationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showMessage('Operation undone successfully', 'success');
      handleCloseUndoSnackbar();
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to undo operation', 'error');
      handleCloseUndoSnackbar();
    },
  });

  const bulkUpdatePricesMutation = useMutation({
    mutationFn: async (data: { productIds: string[]; priceAdjustment: number; adjustmentType: 'percentage' | 'fixed' }) => {
      // Fetch current products to calculate new prices
      const productsResponse = await axiosInstance.get('/products/admin', {
        params: { page: 1, limit: 1000 }
      });
      const allProducts = Array.isArray(productsResponse.data) ? productsResponse.data : productsResponse.data.data || [];

      // Build products array with calculated prices
      const products = data.productIds.map(productId => {
        const product = allProducts.find((p: any) => p.id === productId);
        if (!product) {
          return { id: productId };
        }

        const currentPrice = product.price || 0;
        let newPrice: number;

        if (data.adjustmentType === 'percentage') {
          newPrice = currentPrice * (1 + data.priceAdjustment / 100);
        } else {
          newPrice = currentPrice + data.priceAdjustment;
        }

        return {
          id: productId,
          price: Math.max(0, Math.round(newPrice * 100) / 100) // Round to 2 decimals and ensure non-negative
        };
      });

      return axiosInstance.post('/products/bulk/update', { products });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      const result = response.data;
      setBulkOperationProgress({
        successCount: result.successful || 0,
        failureCount: result.failed || 0,
        total: (result.successful || 0) + (result.failed || 0),
      });
      showMessage(`Updated ${result.successful || 0} products successfully`, 'success');
      if (result.operationId) {
        showUndoSnackbar(result.operationId, 'Bulk price update');
      }
      setSelectedProductIds([]);
      setBulkPriceDialogOpen(false);
      setBulkOperationInProgress(false);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update prices', 'error');
      setBulkOperationInProgress(false);
    },
  });

  const bulkChangeCategoryMutation = useMutation({
    mutationFn: (data: { productIds: string[]; category: string }) =>
      axiosInstance.post('/products/bulk/change-category', { productIds: data.productIds, categoryId: data.category }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      const result = response.data;
      setBulkOperationProgress({
        successCount: result.successful || 0,
        failureCount: result.failed || 0,
        total: (result.successful || 0) + (result.failed || 0),
      });
      showMessage(`Updated ${result.successful || 0} products successfully`, 'success');
      if (result.operationId) {
        showUndoSnackbar(result.operationId, 'Bulk category change');
      }
      setSelectedProductIds([]);
      setBulkCategoryDialogOpen(false);
      setBulkOperationInProgress(false);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to change category', 'error');
      setBulkOperationInProgress(false);
    },
  });

  const bulkAdjustStockMutation = useMutation({
    mutationFn: (data: { productIds: string[]; adjustment: number; operation: 'add' | 'subtract' | 'set' }) => {
      // Transform data to match backend DTO
      let adjustmentValue = data.adjustment;
      if (data.operation === 'subtract') {
        adjustmentValue = -Math.abs(data.adjustment);
      } else if (data.operation === 'add') {
        adjustmentValue = Math.abs(data.adjustment);
      }
      // Note: 'set' operation not supported by backend, will be treated as 'add'

      const adjustments = data.productIds.map(productId => ({
        productId,
        adjustment: adjustmentValue
      }));

      return axiosInstance.post('/products/bulk/adjust-stock', { adjustments });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      const result = response.data;
      setBulkOperationProgress({
        successCount: result.successful || 0,
        failureCount: result.failed || 0,
        total: (result.successful || 0) + (result.failed || 0),
      });
      showMessage(`Updated ${result.successful || 0} products successfully`, 'success');
      if (result.operationId) {
        showUndoSnackbar(result.operationId, 'Bulk stock adjustment');
      }
      setSelectedProductIds([]);
      setBulkStockDialogOpen(false);
      setBulkOperationInProgress(false);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to adjust stock', 'error');
      setBulkOperationInProgress(false);
    },
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: (data: { productIds: string[]; archive: boolean }) =>
      axiosInstance.post('/products/bulk/archive', { productIds: data.productIds, isActive: !data.archive }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      const result = response.data;
      setBulkOperationProgress({
        successCount: result.successful || 0,
        failureCount: result.failed || 0,
        total: (result.successful || 0) + (result.failed || 0),
      });
      showMessage(`Archived ${result.successful || 0} products successfully`, 'success');
      if (result.operationId) {
        showUndoSnackbar(result.operationId, 'Bulk archive');
      }
      setSelectedProductIds([]);
      setBulkArchiveDialogOpen(false);
      setBulkOperationInProgress(false);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to archive products', 'error');
      setBulkOperationInProgress(false);
    },
  });

  const handleVisibilityToggle = (id: string, currentValue: boolean) => {
    const newValue = !currentValue;
    updateMutation.mutate({
      id,
      values: { isVisibleInStorefront: newValue },
    });
  };

  const handleDeleteClick = (id: string) => {
    setProductToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (productToDelete) {
      deleteMutation.mutate(productToDelete);
    }
  };

  const handleSelectionChange = (ids: string[]) => {
    setSelectedProductIds(ids);
  };

  const handleBulkUpdatePrices = () => {
    setBulkPriceDialogOpen(true);
  };

  const handleBulkChangeCategory = () => {
    setBulkCategoryDialogOpen(true);
  };

  const handleBulkAdjustStock = () => {
    setBulkStockDialogOpen(true);
  };

  const handleBulkArchive = () => {
    setBulkArchiveDialogOpen(true);
  };

  const handleSubmitBulkPriceUpdate = () => {
    setBulkOperationInProgress(true);
    bulkUpdatePricesMutation.mutate({
      productIds: selectedProductIds,
      priceAdjustment,
      adjustmentType: priceAdjustmentType,
    });
  };

  const handleSubmitBulkCategoryChange = () => {
    setBulkOperationInProgress(true);
    bulkChangeCategoryMutation.mutate({
      productIds: selectedProductIds,
      category: newCategory,
    });
  };

  const handleSubmitBulkStockAdjustment = () => {
    setBulkOperationInProgress(true);
    bulkAdjustStockMutation.mutate({
      productIds: selectedProductIds,
      adjustment: stockAdjustment,
      operation: stockOperation,
    });
  };

  const handleSubmitBulkArchive = () => {
    setBulkOperationInProgress(true);
    bulkArchiveMutation.mutate({
      productIds: selectedProductIds,
      archive: true,
    });
  };

  const handleInventoryDialogClose = () => {
    setInventoryDialogOpen(false);
    setSelectedProductForInventory(null);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handler for recent search selection
  const handleRecentSearchSelect = (search: RecentSearch) => {
    // Apply search query
    if (search.searchQuery) {
      setSearchValue(search.searchQuery);
    } else {
      setSearchValue('');
    }

    // Apply filters from JSONB
    if (search.filters) {
      if (search.filters.category) {
        setSelectedCategory(search.filters.category);
      } else {
        setSelectedCategory('');
      }
      if (search.filters.minPrice !== undefined) {
        setMinPrice(search.filters.minPrice);
      } else {
        setMinPrice(undefined);
      }
      if (search.filters.maxPrice !== undefined) {
        setMaxPrice(search.filters.maxPrice);
      } else {
        setMaxPrice(undefined);
      }
      if (search.filters.stockStatus) {
        setStockStatus(search.filters.stockStatus);
      } else {
        setStockStatus(StockStatus.ALL);
      }
    } else {
      // Reset filters if no filters in search
      setSelectedCategory('');
      setMinPrice(undefined);
      setMaxPrice(undefined);
      setStockStatus(StockStatus.ALL);
    }
    setPage(0); // Reset to first page
  };

  // Handler for barcode scanner product found
  const handleBarcodeProductFound = (product: Product, _barcode: string) => {
    // Navigate to the product detail page
    navigate(`/app/products/show/${product.id}`);
  };

  // Handle manual barcode input (for USB scanner/douchette)
  const handleBarcodeInputSearch = async (barcode: string) => {
    if (!barcode.trim()) return;

    setBarcodeInput(''); // Clear input immediately

    try {
      const result = await searchProductByBarcode(barcode.trim());
      if (result.found && result.product) {
        handleBarcodeProductFound(result.product, barcode);
      } else {
        showMessage('Produit non trouvé pour ce code-barres', 'warning');
      }
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Erreur de recherche', 'error');
    }

    barcodeInputRef.current?.focus();
  };

  const showUndoSnackbar = (operationId: string, operationType: string) => {
    // Clear any existing timeout
    if (undoTimeoutId) {
      clearTimeout(undoTimeoutId);
    }

    setUndoOperationId(operationId);
    setUndoOperationType(operationType);
    setUndoSnackbarOpen(true);

    // Auto-hide after 5 minutes (300000ms)
    const timeoutId = setTimeout(() => {
      handleCloseUndoSnackbar();
    }, 300000);

    setUndoTimeoutId(timeoutId);
  };

  const handleCloseUndoSnackbar = () => {
    setUndoSnackbarOpen(false);
    setUndoOperationId(null);
    setUndoOperationType('');
    if (undoTimeoutId) {
      clearTimeout(undoTimeoutId);
      setUndoTimeoutId(null);
    }
  };

  const handleUndo = () => {
    if (undoOperationId) {
      undoMutation.mutate(undoOperationId);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutId) {
        clearTimeout(undoTimeoutId);
      }
    };
  }, [undoTimeoutId]);

  // Auto-focus barcode input when page loads (for USB scanner ready)
  useEffect(() => {
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(price || 0);
  };

  // Define columns for ResponsiveTable
  const tableColumns: Column<Product>[] = [
    {
      field: 'name',
      headerName: 'Product Info',
      render: (value, row) => (
        <Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 0.5,
              lineHeight: 1.2,
            }}
          >
            {row.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                color: colors.textMuted,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              SKU: {row.sku || '-'}
            </Typography>
          </Box>
          {row.description && (
            <Typography
              variant="body2"
              sx={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      render: (value) =>
        value ? (
          <Chip
            label={value}
            sx={{
              backgroundColor: alpha(palette.info, 0.1),
              color: palette.info,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${palette.info}`,
              textTransform: 'uppercase',
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            -
          </Typography>
        ),
    },
    {
      field: 'price',
      headerName: 'Price',
      render: (value) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 1,
            borderRadius: theme.radius.xs,
            backgroundColor: alpha(palette.primary, 0.1),
            border: `1px solid ${palette.primary}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.primary,
            }}
          >
            {formatPrice(value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      render: (value) => (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            borderRadius: theme.radius.xs,
            backgroundColor: value
              ? alpha(palette.success, 0.1)
              : alpha(palette.error, 0.1),
            border: `1px solid ${value ? palette.success : palette.error}`,
          }}
        >
          {value ? (
            <CheckCircleIcon sx={{ fontSize: 16, color: palette.success }} />
          ) : (
            <CancelIcon sx={{ fontSize: 16, color: palette.error }} />
          )}
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              color: value ? palette.success : palette.error,
              textTransform: 'uppercase',
            }}
          >
            {value ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isVisibleInStorefront',
      headerName: 'Visibility',
      render: (value, row) => (
        <Tooltip title={value ? 'Hide from storefront' : 'Show in storefront'}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: theme.radius.xs,
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
            }}
          >
            {value ? (
              <VisibilityIcon sx={{ fontSize: 18, color: palette.success }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: 18, color: colors.textMuted }} />
            )}
            <Switch
              checked={value || false}
              onChange={(e) => {
                e.stopPropagation()
                handleVisibilityToggle(row.id, value)
              }}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: palette.success,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: palette.success,
                },
              }}
            />
          </Box>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ ...animations.slideDown, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            PRODUCT CATALOG
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingBagIcon sx={{ fontSize: 40, color: palette.primary }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              PRODUCTS
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/products/create')}
            sx={{
              ...animations.buttonPress,
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
              },
            }}
          >
            New Product
          </Button>
        </Box>
      </Box>

      {/* Product Limit Warning Banner */}
      <LimitWarningBanner resource="products" showProgress />

      {/* Product Tabs */}
      <Card
        sx={{
          ...animations.slideUp(0.05),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          mb: 2,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wide,
              color: colors.textMuted,
              minHeight: 56,
              '&.Mui-selected': {
                color: palette.primary,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: palette.primary,
              height: 3,
            },
          }}
        >
          <Tab
            value="all"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingBagIcon sx={{ fontSize: 18 }} />
                All Products
                <Chip
                  label={tabCounts.all}
                  size="small"
                  sx={{
                    height: 20,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    backgroundColor: activeTab === 'all' ? alpha(palette.primary, 0.1) : colors.surfaceElevated,
                    color: activeTab === 'all' ? palette.primary : colors.textMuted,
                    border: `1px solid ${activeTab === 'all' ? palette.primary : colors.border}`,
                  }}
                />
              </Box>
            }
          />
          <Tab
            value="public"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorefrontIcon sx={{ fontSize: 18 }} />
                Storefront
                <Chip
                  label={tabCounts.public}
                  size="small"
                  sx={{
                    height: 20,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    backgroundColor: activeTab === 'public' ? alpha(palette.success, 0.1) : colors.surfaceElevated,
                    color: activeTab === 'public' ? palette.success : colors.textMuted,
                    border: `1px solid ${activeTab === 'public' ? palette.success : colors.border}`,
                  }}
                />
              </Box>
            }
          />
          {hasB2BAccess && (
            <Tab
              value="b2b"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 18 }} />
                  B2B Catalog
                  <Chip
                    label={tabCounts.b2b}
                    size="small"
                    sx={{
                      height: 20,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.bold,
                      backgroundColor: activeTab === 'b2b' ? alpha(palette.info, 0.1) : colors.surfaceElevated,
                      color: activeTab === 'b2b' ? palette.info : colors.textMuted,
                      border: `1px solid ${activeTab === 'b2b' ? palette.info : colors.border}`,
                    }}
                  />
                </Box>
              }
            />
          )}
        </Tabs>
      </Card>

      {/* Search & Filter Card */}
      <Card
        sx={{
          ...animations.slideUp(0.1),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <ProductSearch
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search products..."
            />
            {/* Recent Searches */}
            <RecentSearches onSearchSelect={handleRecentSearchSelect} limit={5} />
          </Box>
          <IconButton
            onClick={() => setFiltersOpen(true)}
            sx={{
              ...animations.buttonPress,
              backgroundColor: hasAdvancedFilters ? palette.primary : colors.surfaceElevated,
              color: hasAdvancedFilters ? '#FFFFFF' : colors.textPrimary,
              border: `2px solid ${hasAdvancedFilters ? palette.primary : colors.border}`,
              borderRadius: theme.radius.sm,
              px: 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: hasAdvancedFilters ? alpha(palette.primary, 0.8) : alpha(colors.surfaceElevated, 0.7),
                borderColor: palette.primary,
              },
            }}
          >
            <FilterListIcon />
            {hasAdvancedFilters && (
              <Typography
                sx={{
                  ml: 1,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                }}
              >
                Filters Active
              </Typography>
            )}
          </IconButton>
          <TextField
            size="small"
            inputRef={barcodeInputRef}
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleBarcodeInputSearch(barcodeInput);
              }
            }}
            placeholder="Code-barres..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScannerIcon sx={{ color: palette.primary, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                borderRadius: theme.radius.sm,
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: palette.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiOutlinedInput-input': {
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
              },
            }}
          />
          <BarcodeProductSearch
            onProductFound={handleBarcodeProductFound}
            buttonTooltip="Scanner un code-barres pour trouver un produit"
            buttonSx={{
              ...animations.buttonPress,
              backgroundColor: colors.surfaceElevated,
              color: colors.textPrimary,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              px: 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: alpha(colors.surfaceElevated, 0.7),
                borderColor: palette.primary,
              },
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            hideSearch
            data={filteredData}
            columns={exportColumns}
            exportFileName="products"
          />
        </Box>
      </Card>

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCount={selectedProductIds.length}
        onUpdatePrices={handleBulkUpdatePrices}
        onChangeCategory={handleBulkChangeCategory}
        onAdjustStock={handleBulkAdjustStock}
        onArchive={handleBulkArchive}
      />

      {/* Bulk Operation Progress */}
      {bulkOperationProgress && (
        <BulkOperationProgress
          isOperating={bulkOperationInProgress}
          successCount={bulkOperationProgress.successCount}
          failureCount={bulkOperationProgress.failureCount}
          totalItems={bulkOperationProgress.total}
        />
      )}

      {/* Product Catalog Table */}
      <Card
        sx={{
          ...animations.slideUp(0.2),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
        }}
      >
        <ResponsiveTable
          columns={tableColumns}
          rows={filteredData}
          loading={productsLoading}
          emptyMessage="No products match your search criteria"
          primaryField="name"
          statusField="isActive"
          getStatusColor={(status) => (status ? 'success' : 'error')}
          onRowClick={(product) => navigate(`/app/products/show/${product.id}`)}
          onEdit={(product) => navigate(`/app/products/edit/${product.id}`)}
          onDelete={(product) => handleDeleteClick(product.id)}
          selectable={true}
          selectedIds={selectedProductIds}
          onSelectionChange={handleSelectionChange}
        />

        {/* Industrial Pagination - only show when not loading and has data */}
        {!productsLoading && filteredData.length > 0 && (
          <Box
            sx={{
              backgroundColor: colors.surfaceElevated,
              borderTop: `2px solid ${colors.border}`,
            }}
          >
            <TablePagination
              component="div"
              count={data?.total || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  color: colors.textPrimary,
                  fontFamily: typography.fontFamily.mono,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterSpacing.wide,
                },
                '& .MuiSelect-select': {
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.bold,
                },
              }}
            />
          </Box>
        )}
      </Card>

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            backgroundColor: colors.surface,
            borderLeft: `2px solid ${colors.border}`,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.extrabold,
              color: palette.primary,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            Advanced Filters
          </Typography>
          <ProductFilters
            categories={uniqueCategories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            minPrice={minPrice}
            maxPrice={maxPrice}
            onPriceChange={(min, max) => {
              setMinPrice(min);
              setMaxPrice(max);
            }}
            stockStatus={stockStatus}
            onStockStatusChange={setStockStatus}
          />
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSelectedCategory('');
                setMinPrice(undefined);
                setMaxPrice(undefined);
                setStockStatus(StockStatus.ALL);
              }}
              sx={{
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                fontFamily: typography.fontFamily.mono,
                borderColor: colors.border,
                color: colors.textPrimary,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: alpha(palette.primary, 0.1),
                },
              }}
            >
              Clear All
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setShouldSaveSearch(true);
                setFiltersOpen(false);
              }}
              sx={{
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                fontFamily: typography.fontFamily.mono,
                backgroundColor: palette.primary,
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: alpha(palette.primary, 0.8),
                },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.error}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.error,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Delete Product
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ color: colors.textSecondary }}>
            Are you sure you want to delete this product? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Inventory Dialog */}
      <ProductInventoryDialog
        open={inventoryDialogOpen}
        onClose={handleInventoryDialogClose}
        product={selectedProductForInventory}
      />

      {/* Bulk Price Update Dialog */}
      <Dialog
        open={bulkPriceDialogOpen}
        onClose={() => setBulkPriceDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.primary}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.primary,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Update Prices ({selectedProductIds.length} products)
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={priceAdjustmentType}
                onChange={(e) => setPriceAdjustmentType(e.target.value as 'percentage' | 'fixed')}
                label="Adjustment Type"
              >
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="fixed">Fixed Amount (DZD)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Adjustment Value"
              type="number"
              fullWidth
              value={priceAdjustment}
              onChange={(e) => setPriceAdjustment(Number(e.target.value))}
              helperText={
                priceAdjustmentType === 'percentage'
                  ? 'Enter percentage to increase (+) or decrease (-) prices'
                  : 'Enter amount to add (+) or subtract (-) from prices'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setBulkPriceDialogOpen(false)}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitBulkPriceUpdate}
            variant="contained"
            disabled={bulkUpdatePricesMutation.isPending}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
              backgroundColor: palette.primary,
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
              },
            }}
          >
            {bulkUpdatePricesMutation.isPending ? 'Updating...' : 'Update Prices'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Category Change Dialog */}
      <Dialog
        open={bulkCategoryDialogOpen}
        onClose={() => setBulkCategoryDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.primary}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.primary,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Change Category ({selectedProductIds.length} products)
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              label="Category"
            >
              {uniqueCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setBulkCategoryDialogOpen(false)}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitBulkCategoryChange}
            variant="contained"
            disabled={bulkChangeCategoryMutation.isPending || !newCategory}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
              backgroundColor: palette.primary,
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
              },
            }}
          >
            {bulkChangeCategoryMutation.isPending ? 'Updating...' : 'Change Category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Stock Adjustment Dialog */}
      <Dialog
        open={bulkStockDialogOpen}
        onClose={() => setBulkStockDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.primary}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.primary,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Adjust Stock ({selectedProductIds.length} products)
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Operation</InputLabel>
              <Select
                value={stockOperation}
                onChange={(e) => setStockOperation(e.target.value as 'add' | 'subtract' | 'set')}
                label="Operation"
              >
                <MenuItem value="add">Add to stock</MenuItem>
                <MenuItem value="subtract">Subtract from stock</MenuItem>
                <MenuItem value="set">Set stock to</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Adjustment Value"
              type="number"
              fullWidth
              value={stockAdjustment}
              onChange={(e) => setStockAdjustment(Number(e.target.value))}
              helperText={
                stockOperation === 'set'
                  ? 'Set stock quantity to this value'
                  : `${stockOperation === 'add' ? 'Add' : 'Subtract'} this amount from current stock`
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setBulkStockDialogOpen(false)}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitBulkStockAdjustment}
            variant="contained"
            disabled={bulkAdjustStockMutation.isPending}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
              backgroundColor: palette.primary,
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
              },
            }}
          >
            {bulkAdjustStockMutation.isPending ? 'Adjusting...' : 'Adjust Stock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Archive Dialog */}
      <Dialog
        open={bulkArchiveDialogOpen}
        onClose={() => setBulkArchiveDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.error}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.error,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Archive Products ({selectedProductIds.length} products)
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ color: colors.textSecondary }}>
            Are you sure you want to archive {selectedProductIds.length} products? This action can be undone within 5 minutes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={() => setBulkArchiveDialogOpen(false)}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitBulkArchive}
            variant="contained"
            color="error"
            disabled={bulkArchiveMutation.isPending}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            {bulkArchiveMutation.isPending ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo Snackbar */}
      <Snackbar
        open={undoSnackbarOpen}
        autoHideDuration={300000}
        onClose={handleCloseUndoSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseUndoSnackbar}
          severity="info"
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleUndo}
              disabled={undoMutation.isPending}
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
              }}
            >
              {undoMutation.isPending ? 'Undoing...' : 'Undo'}
            </Button>
          }
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.body,
              fontSize: typography.fontSize.sm,
            }}
          >
            {undoOperationType} completed successfully. Click undo to revert (available for 5 minutes).
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};
